import { ChevronLeft } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { CustomEventFeType } from "@intelligems/ig-types/dtos/analytics-events/custom-events";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { officialCategoryName } from "@intelligems/ig-utils/common";
import { CircularProgress, Drawer } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useGetAllCustomEvents } from "@src/api/custom-events/get-all-custom-events";
import { CreateCustomEvent } from "@src/features/customEvents/components/CreateCustomEvent";
import { CustomEventDrawerFooter } from "@src/features/customEvents/components/CustomEventDrawerFooter";
import { CustomEventSubpageWrapper } from "@src/features/customEvents/components/CustomEventSubpageWrapper";
import { SelectExistingCustomMetrics } from "@src/features/customEvents/components/SelectExistingCustomMetrics";
import {
  initCustomEvents,
  initNewCustomEvent,
  resetNewCustomEvent,
  selectedCurrentCustomEvent,
} from "@src/store/slices/customEvents";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";
import {
  getCustomKeyMetricEventIds,
  getCustomMetricIds,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { closeProductPicker } from "@src/store/slices/productPicker";

interface CustomEventsDrawerProps {
  renderLocation: "eventsManager" | "customMetric" | "keyMetric";
  experienceCategory?: ExperienceCategory;
}

export const CustomEventsDrawer = ({
  renderLocation,
  experienceCategory,
}: CustomEventsDrawerProps) => {
  const dispatch = useDispatch();

  const customEvents = useGetAllCustomEvents();
  const currentEvent = useSelector(selectedCurrentCustomEvent);
  const drawer = useSelector(getDrawer("customEvents"));

  // Initialize state directly based on drawerProps and other inputs
  const [isCreating, setIsCreating] = useImmer<boolean>(() => {
    if (drawer?.isEdit) {
      return true;
    }
    if (drawer?.isCreate) {
      return true;
    }
    return renderLocation === "eventsManager"
      ? true
      : currentEvent !== undefined;
  });

  const [drawerPage, setDrawerPage] = useImmer<string>(() => {
    if (drawer?.isEdit) {
      return "event";
    }
    if (drawer?.isCreate) {
      return "subpage";
    }
    return renderLocation === "eventsManager"
      ? "subpage"
      : currentEvent !== undefined
        ? "event"
        : "list";
  });

  const [subpageSelected, setSubpageSelected] =
    useImmer<CustomEventFeType | null>(() => {
      if (drawer?.isEdit && currentEvent?.type) {
        return currentEvent.type as CustomEventFeType;
      }
      return currentEvent?.type
        ? (currentEvent.type as CustomEventFeType)
        : null;
    });
  const customMetricIds = useSelector(getCustomMetricIds);
  const customKeyMetricEventIds = useSelector(getCustomKeyMetricEventIds);
  const [selectedCustomEventIds, setSelectedCustomEventIds] = useImmer<
    string[]
  >(() => {
    if (renderLocation === "eventsManager") {
      return [];
    }
    if (renderLocation === "customMetric") {
      return customMetricIds;
    }
    return customKeyMetricEventIds;
  });

  // State for managing warning dialog
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [selectedEventForWarning, setSelectedEventForWarning] =
    useState<CustomEventFeType | null>(null);

  const handleClose = () => {
    dispatch(resetNewCustomEvent());
    setIsCreating(false);
    setSubpageSelected(null);
    dispatch(closeDrawer("customEvents"));
  };

  const handleBackClick = () => {
    if (drawerPage === "event") {
      if (currentEvent?.id) {
        setIsCreating(false);
        setDrawerPage("list");
      } else {
        setDrawerPage("subpage");
      }
    } else if (drawerPage === "subpage") {
      setDrawerPage("list");
    }

    if (subpageSelected) {
      setSubpageSelected(null);
      dispatch(resetNewCustomEvent());
      dispatch(closeProductPicker());
    } else if (isCreating) {
      setIsCreating(false);
    }
  };

  // Handle showing warning dialog
  const handleShowWarningDialog = (eventKey: CustomEventFeType) => {
    setSelectedEventForWarning(eventKey);
    setShowWarningDialog(true);
  };

  // Handle proceeding after warning dialog
  const handleProceedAfterWarning = () => {
    if (selectedEventForWarning) {
      setSubpageSelected(selectedEventForWarning);
      setDrawerPage("event");
    }
    setShowWarningDialog(false);
    setSelectedEventForWarning(null);
  };

  // Fetch data function
  useEffect(() => {
    if (customEvents?.data?.customEvents) {
      dispatch(
        initCustomEvents({ customEvents: customEvents.data.customEvents })
      );
    }
  }, [customEvents?.data?.customEvents]);

  // Init new event if isCreating is true and a subpage is selected
  useEffect(() => {
    if (isCreating && subpageSelected && !currentEvent) {
      dispatch(initNewCustomEvent({ eventType: subpageSelected }));
    }
  }, [subpageSelected]);

  const shouldRenderBackButton = useMemo(() => {
    return (
      !(
        (renderLocation === "eventsManager" && drawerPage === "subpage") ||
        (renderLocation !== "eventsManager" && drawerPage === "list") ||
        drawer?.isEdit ||
        (drawer?.isCreate && drawerPage === "subpage")
      ) ||
      (drawer?.isCreate && drawerPage === "event")
    );
  }, [renderLocation, drawerPage, drawer]);

  return (
    <Drawer
      anchor="right"
      open={drawer !== undefined}
      onClose={handleClose}
      sx={{
        zIndex: 1400,
        ".MuiDrawer-paper": { borderRadius: "0 !important", width: "680px" },
      }}
    >
      {shouldRenderBackButton && (
        <IgButton
          variant={"text"}
          startIcon={<ChevronLeft size={16} />}
          sx={{
            marginTop: "10px",
            marginLeft: "5px",
            justifyContent: "flex-start",
            width: "fit-content",
            alignItems: "center",
            paddingRight: "15px",
          }}
          onClick={handleBackClick}
        >
          Back
        </IgButton>
      )}
      <Grid
        direction="column"
        container
        sx={{
          height: "100%",
          padding: "0px 40px",
          display: "flex",
          flexWrap: "nowrap",
          marginTop:
            drawer?.isEdit || (drawer?.isCreate && drawerPage === "subpage")
              ? "15px"
              : "0px",
        }}
      >
        {customEvents?.isLoading ? (
          <Grid
            container
            rowSpacing={1}
            direction={"column"}
            justifyContent="center"
            alignItems="center"
            sx={{ height: "100%" }}
          >
            <Grid>
              <IgTypography>Loading Custom Events...</IgTypography>
            </Grid>
            <Grid>
              <CircularProgress size={30} />
            </Grid>
          </Grid>
        ) : (
          <>
            <SelectExistingCustomMetrics
              isCreating={isCreating}
              setIsCreating={setIsCreating}
              customEventsData={customEvents?.data?.customEvents || []}
              selectedCustomEventIds={selectedCustomEventIds}
              setSelectedCustomEventIds={setSelectedCustomEventIds}
              setSubpageSelected={setSubpageSelected}
              setDrawerPage={setDrawerPage}
            />
            <CreateCustomEvent
              setSubpageSelected={setSubpageSelected}
              isCreating={isCreating}
              setDrawerPage={setDrawerPage}
              subpageSelected={subpageSelected}
              handleClose={handleClose}
              onShowWarningDialog={handleShowWarningDialog}
            />
            {subpageSelected && isCreating && drawerPage === "event" && (
              <CustomEventSubpageWrapper eventType={subpageSelected} />
            )}
            {drawerPage === "list" || drawerPage === "event" ? (
              <CustomEventDrawerFooter
                isCreating={isCreating}
                setIsCreating={setIsCreating}
                subpageSelected={subpageSelected}
                setSubpageSelected={setSubpageSelected}
                setSelectedGeneric={setSelectedCustomEventIds}
                renderLocation={renderLocation}
                selectedCustomEventIds={selectedCustomEventIds}
                customEvents={customEvents?.data?.customEvents || []}
                setDrawerPage={setDrawerPage}
              />
            ) : null}
          </>
        )}
      </Grid>
      <IgDialog
        showDialog={showWarningDialog}
        handleClose={() => setShowWarningDialog(false)}
        title="Warning"
        yesButtonText="Continue"
        noButtonText="Cancel"
        handleYesClick={handleProceedAfterWarning}
        handleNoClick={() => setShowWarningDialog(false)}
        maxWidth="sm"
        minWidth="400px"
        dialogContentWidth="400px"
        content={
          <IgTypography>
            {officialCategoryName(experienceCategory || "experiment", true)} has
            already started. This metric cannot be tracked retroactively. Adding
            it now will only capture data going forward, which may affect your
            results analysis.
          </IgTypography>
        }
        sx={{
          zIndex: 1500, // Higher than drawer's 1400 z-index
        }}
      />
    </Drawer>
  );
};
